<script setup>
import { computed, ref, watch } from 'vue';
import { format } from 'date-fns';
import { useI18n } from 'vue-i18n';

import ErpContactsAPI from 'dashboard/api/erpContacts';
import Button from 'dashboard/components-next/button/Button.vue';
import ButtonGroup from 'dashboard/components-next/buttonGroup/ButtonGroup.vue';
import { useAlert } from 'dashboard/composables';
import { useConfig } from 'dashboard/composables/useConfig';
import { useStore } from 'dashboard/composables/store';
import WootDropdownItem from 'shared/components/ui/dropdown/DropdownItem.vue';
import WootDropdownMenu from 'shared/components/ui/dropdown/DropdownMenu.vue';

import ErpInvoicesDialog from './ErpInvoicesDialog.vue';
import ErpPaymentPromiseDialog from './ErpPaymentPromiseDialog.vue';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
  contact: { type: Object, required: true },
});

const { t } = useI18n();
const store = useStore();
const { erpProvider, erpProviderName } = useConfig();

const showActionsDropdown = ref(false);
const invoicesDialogRef = ref(null);
const paymentPromiseDialogRef = ref(null);
const invoices = ref([]);
const isLoadingInvoices = ref(false);
const isSubmittingPromise = ref(false);
const errorCode = ref('');
const invoiceRequestGeneration = ref(0);
const activeInvoiceId = ref(null);
const invoiceDetailsById = ref({});
const invoiceDetailGeneration = ref(0);
const minimumDate = ref(format(new Date(), 'yyyy-MM-dd'));

const isVisible = computed(
  () =>
    Boolean(erpProvider?.trim()) &&
    Boolean(erpProviderName?.trim()) &&
    Boolean(props.contact.custom_attributes?.ERP_CLIENT_ID?.toString().trim())
);
const selectedExternalId = computed(() =>
  store.getters['erpContacts/getSelectedExternalId'](props.contact.id)
);
const selectedSnapshot = computed(() =>
  store.getters['erpContacts/getSelectedSnapshot'](props.contact.id)
);
const hasSelectedService = computed(() =>
  Boolean(selectedExternalId.value?.toString().trim())
);
const hasSummary = computed(() => Boolean(selectedSnapshot.value?.synced_at));
const status = computed(() => {
  const value = selectedSnapshot.value?.service_status;
  return value == null || value === '' ? '' : value.toString();
});
const serviceLabel = computed(() =>
  [selectedExternalId.value, selectedSnapshot.value?.username]
    .filter(Boolean)
    .join(' · ')
);
const balance = computed(() => {
  const value = selectedSnapshot.value?.pending_balance;
  return value == null || value === '' ? '' : Number(value).toFixed(2);
});
const clearInvoiceQuery = () => {
  invoices.value = [];
  errorCode.value = '';
};

const clearInvoiceDetails = () => {
  invoiceDetailGeneration.value += 1;
  activeInvoiceId.value = null;
  invoiceDetailsById.value = {};
};

const setInvoiceDetail = (invoiceId, detail) => {
  invoiceDetailsById.value = {
    ...invoiceDetailsById.value,
    [invoiceId]: detail,
  };
};

const fetchInvoiceDescription = async (invoiceId, { force = false } = {}) => {
  const existing = invoiceDetailsById.value[invoiceId];
  if (!force && (existing?.loaded || existing?.isLoading)) return;

  const requestGeneration = invoiceDetailGeneration.value;
  const requestContext = {
    conversationId: props.conversationId,
    contactId: props.contact.id,
    externalServiceId: selectedExternalId.value,
  };
  const isCurrentRequest = () =>
    requestGeneration === invoiceDetailGeneration.value &&
    requestContext.conversationId === props.conversationId &&
    requestContext.contactId === props.contact.id &&
    requestContext.externalServiceId === selectedExternalId.value;

  setInvoiceDetail(invoiceId, {
    isLoading: true,
    loaded: false,
    items: null,
    errorCode: '',
  });
  try {
    const { data } = await ErpContactsAPI.invoiceDescription(
      requestContext.conversationId,
      requestContext.externalServiceId,
      invoiceId
    );
    if (!isCurrentRequest()) return;

    setInvoiceDetail(invoiceId, {
      isLoading: false,
      loaded: true,
      items: data.items,
      errorCode: '',
    });
  } catch (error) {
    if (!isCurrentRequest()) return;

    setInvoiceDetail(invoiceId, {
      isLoading: false,
      loaded: false,
      items: null,
      errorCode: error.response?.data?.error || 'provider_error',
    });
  }
};

const toggleInvoice = invoiceId => {
  if (activeInvoiceId.value === invoiceId) {
    activeInvoiceId.value = null;
    return;
  }

  activeInvoiceId.value = invoiceId;
  const detail = invoiceDetailsById.value[invoiceId];
  if (!detail?.loaded && !detail?.isLoading) fetchInvoiceDescription(invoiceId);
};

const retryInvoiceDescription = invoiceId => {
  fetchInvoiceDescription(invoiceId, { force: true });
};

const fetchInvoices = async () => {
  invoiceRequestGeneration.value += 1;
  const requestGeneration = invoiceRequestGeneration.value;
  const requestContext = {
    conversationId: props.conversationId,
    externalServiceId: selectedExternalId.value,
  };
  const isCurrentRequest = () =>
    requestGeneration === invoiceRequestGeneration.value &&
    requestContext.conversationId === props.conversationId &&
    requestContext.externalServiceId === selectedExternalId.value;

  clearInvoiceQuery();
  isLoadingInvoices.value = true;
  try {
    const { data } = await ErpContactsAPI.invoices(
      requestContext.conversationId,
      requestContext.externalServiceId
    );
    if (!isCurrentRequest()) return;

    invoices.value = [...data.invoices].sort(
      (left, right) =>
        left.due_on.localeCompare(right.due_on) ||
        left.external_id.localeCompare(right.external_id)
    );
  } catch (error) {
    if (!isCurrentRequest()) return;

    invoices.value = [];
    errorCode.value = error.response?.data?.error || 'provider_error';
  } finally {
    if (isCurrentRequest()) isLoadingInvoices.value = false;
  }
};

watch(
  [() => props.conversationId, () => props.contact.id, selectedExternalId],
  () => {
    invoiceRequestGeneration.value += 1;
    clearInvoiceQuery();
    clearInvoiceDetails();
    isLoadingInvoices.value = false;
  }
);

const openInvoices = () => {
  showActionsDropdown.value = false;
  clearInvoiceQuery();
  clearInvoiceDetails();
  invoicesDialogRef.value?.open();
  fetchInvoices();
};

const openPaymentPromise = () => {
  showActionsDropdown.value = false;
  minimumDate.value = format(new Date(), 'yyyy-MM-dd');
  clearInvoiceQuery();
  paymentPromiseDialogRef.value?.open();
  fetchInvoices();
};

const submitPaymentPromise = async form => {
  isSubmittingPromise.value = true;
  errorCode.value = '';
  try {
    await ErpContactsAPI.createPaymentPromise(props.conversationId, {
      external_client_id: selectedExternalId.value,
      invoice_id: form.invoiceId,
      limit_on: form.limitOn,
      comment: form.comment,
    });
    paymentPromiseDialogRef.value?.closeAfterSuccess();
    useAlert(t('CONVERSATION_SIDEBAR.ERP.PAYMENT_PROMISE.SUCCESS'));
  } catch (error) {
    errorCode.value = error.response?.data?.error || 'provider_error';
    if (errorCode.value === 'invoice_not_found') invoices.value = [];
  } finally {
    isSubmittingPromise.value = false;
  }
};
</script>

<template>
  <div v-if="isVisible" class="relative flex min-w-0 items-center gap-2">
    <div
      v-if="hasSummary && (serviceLabel || status || balance)"
      data-testid="erp-summary"
      class="hidden min-w-0 items-center gap-2 text-xs text-n-slate-11 md:flex"
    >
      <span
        v-if="serviceLabel"
        data-testid="erp-summary-service"
        class="max-w-48 truncate"
      >
        {{ serviceLabel }}
      </span>
      <span
        v-if="status"
        data-testid="erp-summary-status"
        class="max-w-32 truncate border-s border-n-weak ps-2"
      >
        {{ status }}
      </span>
      <span
        v-if="balance"
        data-testid="erp-summary-balance"
        class="whitespace-nowrap border-s border-n-weak ps-2"
      >
        {{ t('CONVERSATION_SIDEBAR.ERP.HEADER.BALANCE', { balance }) }}
      </span>
    </div>
    <ButtonGroup
      class="flex-shrink-0 rounded-lg shadow outline outline-1 outline-n-container"
    >
      <Button
        data-testid="erp-provider-primary"
        size="sm"
        color="slate"
        no-animation
        class="ltr:rounded-r-none rtl:rounded-l-none !outline-0"
        :label="erpProviderName"
        :disabled="!hasSelectedService"
        :aria-label="
          t('CONVERSATION_SIDEBAR.ERP.HEADER.OPEN_INVOICES', {
            provider: erpProviderName,
          })
        "
        @click="openInvoices"
      />
      <Button
        data-testid="erp-menu-toggle"
        icon="i-lucide-chevron-down"
        size="sm"
        color="slate"
        no-animation
        trailing-icon
        class="ltr:rounded-l-none rtl:rounded-r-none !outline-0"
        :disabled="!hasSelectedService"
        :aria-label="
          t('CONVERSATION_SIDEBAR.ERP.HEADER.OPEN_ACTIONS', {
            provider: erpProviderName,
          })
        "
        @click="showActionsDropdown = true"
      />
    </ButtonGroup>
    <div
      v-if="showActionsDropdown"
      v-on-clickaway="() => (showActionsDropdown = false)"
      class="absolute end-0 top-full z-10 mt-0.5 box-content w-fit min-w-[11rem] max-w-[14rem] rounded-lg border border-n-strong bg-n-alpha-3 p-2 shadow-lg backdrop-blur-[100px] [&_ul>li]:mb-0"
    >
      <WootDropdownMenu class="mb-0">
        <WootDropdownItem>
          <Button
            data-testid="erp-payment-promise-action"
            ghost
            slate
            sm
            start
            class="w-full"
            icon="i-lucide-calendar-check"
            :label="t('CONVERSATION_SIDEBAR.ERP.ACTIONS.PAYMENT_PROMISE')"
            @click="openPaymentPromise"
          />
        </WootDropdownItem>
        <WootDropdownItem>
          <Button
            data-testid="erp-pending-invoices-action"
            ghost
            slate
            sm
            start
            class="w-full"
            icon="i-lucide-receipt-text"
            :label="t('CONVERSATION_SIDEBAR.ERP.ACTIONS.PENDING_INVOICES')"
            @click="openInvoices"
          />
        </WootDropdownItem>
      </WootDropdownMenu>
    </div>

    <ErpInvoicesDialog
      ref="invoicesDialogRef"
      :invoices="invoices"
      :is-loading="isLoadingInvoices"
      :error-code="errorCode"
      :active-invoice-id="activeInvoiceId"
      :invoice-details-by-id="invoiceDetailsById"
      @toggle-invoice="toggleInvoice"
      @retry-description="retryInvoiceDescription"
      @retry="fetchInvoices"
      @close="clearInvoiceDetails"
    />
    <ErpPaymentPromiseDialog
      ref="paymentPromiseDialogRef"
      :invoices="invoices"
      :is-loading="isLoadingInvoices"
      :is-submitting="isSubmittingPromise"
      :error-code="errorCode"
      :minimum-date="minimumDate"
      @retry="fetchInvoices"
      @submit="submitPaymentPromise"
    />
  </div>
</template>
